import { computed, inject, ref } from 'vue';
import { useAccountStore } from 'src/stores/account';
import { useI18n } from 'vue-i18n';
import { layoutIntPlugin } from 'src/plugins/i18n/local/layouts';
import { AppGeneralVariables } from 'src/plugins/general/variables';
import { AppStringMethods } from 'src/plugins/string/stringMethods';
/**
 * Standard options for drawers and stuff
 */
const DrawerOptionsData = () => {
    const { t } = layoutIntPlugin();
    const { t: $t } = useI18n({
        useScope: 'global',
        locale: 'en-US',
        fallbackLocale: 'en-US',
    });
    const backendRoute = inject('host-backend-route', 'host_dashboard');
    const { isAdmin, isHost } = AppGeneralVariables();
    const { numberWithCommas } = AppStringMethods();
    const accountStore = useAccountStore();
    const bookingsCount = computed(() => accountStore.custBookings);
    const orderCount = computed(() => accountStore.hostOrder);
    const profileCashback = computed(() => {
        return accountStore.currentCountryFlexCredits;
    });
    const user = computed(() => accountStore.user);
    const appBadge = ref({
        common: 0,
        getspaces: bookingsCount.value + orderCount.value,
        getproperty: 0,
    });
    const drawerOptionShow = ref({
        common: false,
        getspaces: true,
        getproperty: false,
    });
    // options found for all types of dropdowns
    const commonOptions = computed(() => [
        {
            icon: 'perm_contact_calendar',
            routerData: { name: 'Contact Us' },
            showCondition: true,
            title: $t('user.Contact Us'),
        },
        {
            icon: 'policy',
            routerData: {
                name: 'Policies Page',
                query: { tabs: 'Privacy Policy' },
            },
            showCondition: true,
            title: $t('pages.Policies'),
        },
        {
            icon: 'power_off',
            showCondition: user.value !== null,
            title: $t('user.Logout'),
        },
        {
            icon: 'person_add',
            showCondition: user.value === null,
            title: $t('user.Sign Up'),
        },
        {
            icon: 'storefront',
            routerData: { name: 'Rent Out Spaces Flexibly' },
            showCondition: !isHost.value,
            title: t('user.become'),
        },
        {
            icon: 'login',
            showCondition: user.value === null,
            title: $t('user.Login'),
        },
    ]);
    // standard stuffs present in the drawer
    const drawerOptions = computed(() => {
        const allOptions = {
            common: [
                {
                    additionalData: undefined,
                    badgeCount: bookingsCount.value,
                    category: 'dashboard',
                    defaultOpened: true,
                    icon: 'dashboard',
                    routerData: { name: 'User Dashboard' },
                    show: user.value !== null,
                    shownLen: 0,
                    showOnOuter: true,
                    title: $t('dashboard.Dashboard'),
                    type: 'route',
                    items: []
                },
            ],
            getspaces: [
                {
                    additionalData: {
                        value: profileCashback.value
                            ? `$ ${numberWithCommas(Number(profileCashback.value.country_reward_bal).toFixed(2))}`
                            : '$0.00',
                        sidePosColor: 'positive',
                        position: 'side'
                    },
                    badgeCount: 0,
                    category: 'credit',
                    defaultOpened: false,
                    icon: 'redeem',
                    routerData: { name: 'Account Rewards' },
                    show: true,
                    shownLen: 0,
                    showOnOuter: true,
                    title: $t('Credits'),
                    type: 'route',
                    items: []
                },
                {
                    additionalData: undefined,
                    badgeCount: bookingsCount.value,
                    category: 'account',
                    defaultOpened: false,
                    icon: 'grading',
                    routerData: undefined,
                    show: user.value !== null,
                    shownLen: 1,
                    showOnOuter: false,
                    title: $t('user.Account'),
                    type: 'list',
                    items: [
                        {
                            icon: 'grading',
                            routerData: { name: 'Upcoming Orders' },
                            showCondition: true,
                            title: t('user.upcoming'),
                        }
                    ]
                },
                {
                    additionalData: undefined,
                    badgeCount: 0,
                    category: 'account',
                    defaultOpened: false,
                    icon: 'group',
                    routerData: undefined,
                    show: !isHost.value,
                    shownLen: !isHost.value ? 1 : 0,
                    showOnOuter: false,
                    title: '',
                    type: 'list',
                    items: [
                        {
                            icon: 'storefront',
                            routerData: { name: 'Rent Out Spaces Flexibly' },
                            showCondition: !isHost.value,
                            title: t('user.become'),
                        },
                    ],
                },
                {
                    additionalData: undefined,
                    badgeCount: orderCount.value,
                    category: 'host',
                    defaultOpened: true,
                    icon: 'location_city',
                    routerData: undefined,
                    show: isHost.value,
                    shownLen: 2,
                    showOnOuter: false,
                    title: $t('Host'),
                    type: 'expand',
                    items: [
                        {
                            additionalData: {
                                title: t('host.New Orders'),
                                value: (orderCount.value || '').toString(),
                                position: ''
                            },
                            badgeCount: orderCount.value,
                            icon: 'assignment_turned_in',
                            routerData: {
                                name: backendRoute === 'admin_host_dashboard' && isAdmin.value
                                    ? 'Admin Host Dashboard'
                                    : 'Host Dashboard'
                            },
                            showCondition: true,
                            title: $t('dashboard.Dashboard'),
                        },
                        {
                            icon: 'date_range',
                            showCondition: true,
                            title: t('host.Booking Calendar'),
                        },
                    ],
                },
            ]
        };
        let parsedOptions = {};
        const activatedApps = JSON.parse(process.env.activated_apps);
        for (const key in allOptions) {
            if (key !== 'common' &&
                activatedApps.indexOf(key) === -1)
                continue;
            const typedKey = key;
            parsedOptions = {
                ...parsedOptions,
                [typedKey]: allOptions[typedKey]
            };
        }
        return parsedOptions;
    });
    return { appBadge, commonOptions, drawerOptions, drawerOptionShow };
};
export { DrawerOptionsData };
